/**
 * NMS PDP Size Integration Component
 *
 * Integrates NMSSizePopup with Product Detail Page add-to-cart functionality.
 * Intercepts add-to-cart clicks when size options exist and opens size popup
 * for streamlined size selection experience.
 *
 * Built following NMS JavaScript standards - extends NMSBaseClass,
 * uses window.neemans namespace, and integrates with existing theme systems.
 */

class NMSPDPSizeIntegration extends window.NMSBaseClass {
  constructor(element) {
    super();

    // Core properties
    this.element = element;
    this.productId = element.dataset.productId;
    this.formId = element.dataset.formId;
    this.sectionId = element.dataset.sectionId;

    // Component state
    this.productData = null;
    this.hasSize = false;
    this.sizePopup = null;
    this.isProcessing = false;

    // Store in global namespace
    window.neemans = window.neemans || {};
    window.neemans.pdpSizeIntegration = window.neemans.pdpSizeIntegration || {};
    window.neemans.pdpSizeIntegration[this.productId] = this;

    // Bind event handlers
    this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
    this.handleSizeSelection = this.handleSizeSelection.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  init() {
    super.init();

    try {
      this.#setupRefs();
      this.#parseProductData();
      this.#bindEvents();
      this.#checkSizeOptions();
    } catch (error) {
      // Silent fail - initialization error
    }
  }

  /**
   * Refresh element references (called after DOM updates)
   * @public
   */
  refreshRefs() {
    try {
      // Remove old event listener from old button if it exists
      if (this.refs.addButton) {
        this.refs.addButton.removeEventListener('click', this.handleAddToCartClick);
      }

      // Re-setup refs with new DOM elements
      this.#setupRefs();

      // Re-bind event to new button
      if (this.refs.addButton) {
        this.refs.addButton.addEventListener('click', this.handleAddToCartClick);
      }
    } catch (error) {
      console.error('NMSPDPSizeIntegration: Failed to refresh refs:', error);
    }
  }

  /**
   * Setup element references
   * @private
   */
  #setupRefs() {
    // Get the add button first
    this.refs.addButton = this.element.querySelector('.nms-atc-button');

    if (!this.refs.addButton) {
      throw new Error('Add to cart button not found');
    }

    // Get the form that contains THIS button for submission
    this.refs.form = this.refs.addButton.closest('form');

    if (!this.refs.form) {
      throw new Error(`Product form not found for button`);
    }

    // IMPORTANT: There are TWO forms with the same ID on the page (main + sticky bar)
    // For checking variant selection, we need to check the MAIN form (first one)
    // because that's where the variant picker is and where the user selects sizes
    const allFormsWithId = document.querySelectorAll(`form[id="${this.formId}"]`);
    this.refs.mainForm = allFormsWithId[0]; // First form is the main one

    // Get variant input from the MAIN form (not the button's own form)
    this.refs.variantIdInput = this.refs.mainForm?.querySelector('input[name="id"]');

    // Variant picker integration
    this.refs.variantPicker = document.querySelector(`[data-product-id="${this.productId}"]`);
  }

  /**
   * Parse product data from JSON script
   * @private
   */
  #parseProductData() {
    const dataScript = this.element.querySelector('[data-pdp-size-data]');

    if (!dataScript) {
      throw new Error('Product data script not found');
    }

    try {
      this.productData = JSON.parse(dataScript.textContent);

      if (!this.productData || !this.productData.variants) {
        throw new Error('Invalid product data structure');
      }
    } catch (error) {
      throw new Error(`Failed to parse product data: ${error.message}`);
    }
  }

  /**
   * Bind event handlers
   * @private
   */
  #bindEvents() {
    // Add to cart button click - use normal bubbling phase (after theme handler)
    // This way we only intercept if theme didn't handle it
    this.refs.addButton.addEventListener('click', this.handleAddToCartClick);

    // Listen for variant changes from variant picker
    document.addEventListener('nms:variant-changed', this.handleVariantChange);

    // Listen for theme variant changes
    document.addEventListener('variant:change', this.handleVariantChange);
  }

  /**
   * Check if product has size options
   * @private
   */
  #checkSizeOptions() {
    if (!this.productData.options) {
      this.hasSize = false;
      return;
    }

    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];

    this.hasSize = this.productData.options.some(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  /**
   * Handle add to cart button click
   *
   * Flow:
   * 1. Always check the MAIN form for variant selection (both main & sticky bar buttons)
   * 2. If variant is selected: sync to button's form and allow submission
   * 3. If no variant: show size popup
   *
   * @param {Event} event - Click event
   */
  handleAddToCartClick(event) {
    // If no size option, allow normal form submission
    if (!this.hasSize || this.isProcessing) {
      return;
    }

    // Refresh refs to ensure we have the latest form state
    // This is important for sticky bar buttons that might be clicked after variant changes
    this.refreshRefs();

    // Check if all required selections are made (checks MAIN form)
    const currentVariant = this.#getCurrentVariant();

    // Check if size is already selected (checks MAIN form)
    if (this.#isSizeSelected(currentVariant)) {
      // Size already selected in main form
      // Sync the variant ID from main form to this button's form (important for sticky bar)
      const mainVariantId = this.refs.variantIdInput?.value;
      const currentFormVariantInput = this.refs.form.querySelector('input[name="id"]');

      if (currentFormVariantInput && mainVariantId && currentFormVariantInput.value !== mainVariantId) {
        currentFormVariantInput.value = mainVariantId;
      }

      // Allow normal form submission
      return;
    }

    // No size selected in main form - prevent form submission and open size popup
    event.preventDefault();
    event.stopPropagation();

    this.#openSizePopup();
  }

  /**
   * Get current variant based on form state
   * @returns {Object|null} Current variant
   * @private
   */
  #getCurrentVariant() {
    const variantId = this.refs.variantIdInput?.value;

    if (!variantId || variantId === 'undefined' || variantId === '') {
      return null;
    }

    return this.productData.variants.find(variant => variant.id.toString() === variantId);
  }

  /**
   * Check if size is already selected for current variant
   * @param {Object} variant - Current variant (unused, kept for API compatibility)
   * @returns {boolean} True if size is selected
   * @private
   */
  #isSizeSelected(variant) {
    // Simple check: is any size option selected by checking the variant ID input
    const variantId = this.refs.variantIdInput?.value;

    // If there's a valid variant ID (not empty, undefined, or null), size is selected
    const hasValidVariant = variantId && variantId !== '' && variantId !== 'undefined';

    return hasValidVariant;
  }

  /**
   * Find size option in product options
   * @returns {Object|null} Size option
   * @private
   */
  #findSizeOption() {
    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];

    return this.productData.options.find(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  /**
   * Open size popup for size selection
   * @private
   */
  #openSizePopup() {
    // Create popup if it doesn't exist
    if (!this.sizePopup && window.NMSSizePopup) {
      this.sizePopup = new NMSSizePopup({
        productId: this.productId,
        productData: this.productData,
        triggerElement: this.refs.addButton,
        containerElement: document.body,
        onSelect: this.handleSizeSelection,
        onClose: () => {
          // Hide overlay when popup closes
          const overlay = document.querySelector('.nms-overlay');
          if (overlay) {
            overlay.classList.remove('nms-overlay--size-popup-active');
          }
        },
      });
      this.sizePopup.init();
    }

    // Update product data and show popup
    if (this.sizePopup) {
      this.sizePopup.updateProductData(this.productData);
      this.sizePopup.show();
    }

    // Show overlay
    const overlay = document.querySelector('.nms-overlay');
    if (overlay) {
      overlay.classList.add('nms-overlay--size-popup-active');
    }
  }

  /**
   * Handle size selection from popup
   * @param {Object} selection - Size selection data
   */
  handleSizeSelection(selection) {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      // Find matching variant with selected size
      const matchingVariant = this.#findVariantWithSize(selection);

      if (matchingVariant && matchingVariant.available) {
        // Update form with selected variant and UI
        this.#updateVariantInForm(matchingVariant);

        // Brief delay to show UI update, then submit
        setTimeout(() => {
          this.#submitForm();
        }, 600); // Brief delay to show size selection
      } else {
        this.isProcessing = false;
      }
    } catch (error) {
      this.isProcessing = false;
    }
  }

  /**
   * Find variant matching size selection
   * @param {Object} selection - Size selection from popup
   * @returns {Object|null} Matching variant
   * @private
   */
  #findVariantWithSize(selection) {
    return this.productData.variants.find(variant => {
      const optionKey = `option${selection.optionPosition}`;
      return variant[optionKey] === selection.size && variant.available;
    });
  }

  /**
   * Update form with selected variant
   * @param {Object} variant - Selected variant
   * @private
   */
  #updateVariantInForm(variant) {
    // Update the main form's variant input
    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = variant.id;
    }

    // Also update the button's own form if it's different (e.g., sticky bar)
    const currentFormVariantInput = this.refs.form.querySelector('input[name="id"]');
    if (currentFormVariantInput && currentFormVariantInput !== this.refs.variantIdInput) {
      currentFormVariantInput.value = variant.id;
    }

    // Update the variant picker UI to show selected size
    this.#updateVariantPickerUI(variant);

    // Dispatch variant change event for other components
    const variantChangeEvent = new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variant: variant,
        source: 'size-popup',
      },
    });

    document.dispatchEvent(variantChangeEvent);
  }

  /**
   * Update variant picker UI to reflect selected size
   * @param {Object} variant - Selected variant
   * @private
   */
  #updateVariantPickerUI(variant) {
    try {
      const formElement = document.getElementById(this.formId);
      if (!formElement) return;

      // Find the size option that matches the selected variant
      const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];
      
      // Get the size value from the variant (e.g., "UK 8" -> "8")
      const selectedSize = variant.option1 || variant.title;
      const cleanSize = selectedSize.replace('UK ', '').replace('US ', '').replace('EU ', '');
      
      // Find all size option inputs in the document (not just in form)
      const optionInputs = document.querySelectorAll(`input[data-option-position][form="${this.formId}"]`);
      
      for (const input of optionInputs) {
        const fieldset = input.closest('[data-picker-id]');
        const legend = fieldset?.querySelector('legend');
        const optionName = legend?.textContent?.toLowerCase() || '';
        
        // Check if this is a size option
        if (sizeLabels.some(label => optionName.includes(label))) {
          const label = input.nextElementSibling || input.closest('label');
          const inputText = label?.textContent?.trim() || '';
          
          // Check if this input matches the selected size
          if (inputText === cleanSize || inputText === selectedSize) {
            // Select this input
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            
            // Update visual states
            if (label && label.classList.contains('block-swatch')) {
              // Remove is-selected from all other swatches in this option group
              const allSwatches = fieldset.querySelectorAll('.block-swatch');
              allSwatches.forEach(swatch => swatch.classList.remove('is-selected'));
              
              // Add is-selected to this swatch
              label.classList.add('is-selected');
            }
            
            // Update the display value
            const valueSpan = fieldset?.querySelector('.nms-variant__value');
            if (valueSpan) {
              valueSpan.innerHTML = inputText; // Use innerHTML to replace placeholder
            }
            
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error updating variant picker UI:', error);
    }
  }

  /**
   * Submit the form programmatically
   * @private
   */
  #submitForm() {
    if (!this.refs.form) {
      console.error('NMSPDPSizeIntegration: Cannot submit - form ref is null');
      this.isProcessing = false;
      return;
    }

    if (!this.refs.addButton) {
      console.error('NMSPDPSizeIntegration: Cannot submit - addButton ref is null');
      this.isProcessing = false;
      return;
    }

    try {
      // Don't add loading state before submission - let the theme handle it
      // The theme's product-form will handle loading states during submission

      // Check if button is actually inside the form
      if (this.refs.form.contains(this.refs.addButton)) {
        // Button is inside form, use requestSubmit with submitter
        if (HTMLFormElement.prototype.requestSubmit) {
          this.refs.form.requestSubmit(this.refs.addButton);
        } else {
          this.refs.addButton.click();
        }
      } else {
        // Button is outside form (or form attribute is used), trigger directly via click
        this.refs.addButton.click();
      }

      // Reset processing state immediately since theme handles submission
      this.isProcessing = false;
    } catch (error) {
      console.error('NMSPDPSizeIntegration: Form submission error', error);
      this.isProcessing = false;
    }
  }

  /**
   * Handle variant changes from variant picker or other sources
   * @param {Event} event - Variant change event
   */
  handleVariantChange() {
    // Update product data if variant picker provides new data
    const variantPickerInstance = window.neemans?.variantPicker?.[this.sectionId];

    if (variantPickerInstance) {
      // Sync with variant picker state
      const currentVariant = this.#getCurrentVariant();

      if (currentVariant) {
        // Update size popup if it exists
        if (this.sizePopup) {
          this.sizePopup.updateProductData(this.productData);
        }
      }
    }
  }

  /**
   * Clean up component
   */
  destroy() {
    super.destroy();

    // Clean up size popup
    if (this.sizePopup) {
      this.sizePopup.destroy();
      this.sizePopup = null;
    }

    // Remove event listeners
    if (this.refs.addButton) {
      this.refs.addButton.removeEventListener('click', this.handleAddToCartClick);
    }

    document.removeEventListener('nms:variant-changed', this.handleVariantChange);
    document.removeEventListener('variant:change', this.handleVariantChange);

    // Remove the data-initialized attribute so it can be re-initialized if needed
    if (this.element) {
      this.element.removeAttribute('data-initialized');
    }

    // Clean up global reference
    if (window.neemans.pdpSizeIntegration?.[this.productId]) {
      delete window.neemans.pdpSizeIntegration[this.productId];
    }
  }

}

// Auto-initialization function
function initNMSPDPSizeIntegration() {
  // Check for required dependencies
  if (!window.NMSBaseClass) {
    return;
  }

  const elements = document.querySelectorAll('[data-nms-pdp-size-integration]');

  for (const element of elements) {
    // Skip if already initialized
    if (!element.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSPDPSizeIntegration(element);
        instance.init();
        element.setAttribute('data-initialized', 'true');
      } catch (error) {
        // Silent fail - initialization error
      }
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSPDPSizeIntegration);
} else {
  initNMSPDPSizeIntegration();
}

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container } = event.detail;
  
  // Find PDP size integration elements in the updated container and reinitialize
  const pdpSizeElements = container.querySelectorAll('[data-nms-pdp-size-integration]:not([data-initialized])');
  
  for (const element of pdpSizeElements) {
    try {
      const instance = new NMSPDPSizeIntegration(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      // Silent fail - re-initialization error
    }
  }
});

// Handle Shopify theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-pdp-size-integration]')) {
    initNMSPDPSizeIntegration();
  }
});

// CRITICAL: Re-initialize when product-rerender replaces buy-buttons
// The product-rerender component replaces the buy-buttons element when variants change
// This causes the ATC button to lose its event listeners
// We need to detect this and re-initialize
const observePDPButtonReplacements = () => {
  // Use MutationObserver to detect when buy-buttons elements are added
  const observer = new MutationObserver(mutations => {
    let needsReinit = false;

    for (const mutation of mutations) {
      // Check if any added nodes are or contain buy-buttons
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.matches && node.matches('buy-buttons')) {
            needsReinit = true;
            break;
          } else if (node.querySelector && node.querySelector('buy-buttons')) {
            needsReinit = true;
            break;
          }
        }
      }

      if (needsReinit) break;
    }

    if (needsReinit) {
      // Longer delay to ensure DOM is fully updated after product-rerender
      setTimeout(() => {
        // First, try to refresh refs on existing instances
        if (window.neemans.pdpSizeIntegration) {
          Object.entries(window.neemans.pdpSizeIntegration).forEach(([productId, instance]) => {
            // More robust check: look for container by product ID in current DOM
            const containerInDOM = document.querySelector(`[data-nms-pdp-size-integration][data-product-id="${productId}"]`);

            if (containerInDOM) {
              // Container exists in DOM, refresh refs to get new button references
              // Update the element reference if it changed
              if (instance.element !== containerInDOM) {
                instance.element = containerInDOM;
              }

              instance.refreshRefs();
            } else {
              // Container no longer exists in DOM, clean up orphaned instance
              instance.destroy();
            }
          });
        }

        // Remove data-initialized from replaced containers so they can be re-initialized if needed
        document.querySelectorAll('[data-nms-pdp-size-integration][data-initialized]').forEach(container => {
          // Check if this container still has working event listeners
          const hasInstance = container.dataset.productId && window.neemans.pdpSizeIntegration?.[container.dataset.productId];
          if (!hasInstance) {
            container.removeAttribute('data-initialized');
          }
        });

        // Initialize any new containers
        initNMSPDPSizeIntegration();
      }, 100);
    }
  });

  // Observe the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// Start observing after initial load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observePDPButtonReplacements);
} else {
  observePDPButtonReplacements();
}

// Export for manual initialization if needed
window.NMSPDPSizeIntegration = NMSPDPSizeIntegration;
window.initNMSPDPSizeIntegration = initNMSPDPSizeIntegration;
